import { useEffect, useRef, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { withViewTransition } from '@/lib/viewTransition'

interface Props {
  dateKey: string
  onPrev: () => void
  onNext: () => void
  children: ReactNode
}

const THRESHOLD_PX = 60
const LOCK_DISTANCE = 12

export function SwipeableDay({ dateKey, onPrev, onNext, children }: Props) {
  const [dragX, setDragX] = useState(0)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const lock = useRef<'h' | 'v' | null>(null)
  const currentDragX = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setDragX(0) }, [dateKey])

  const onTouchStart = useCallback((e: TouchEvent) => {
    const t = e.touches[0]
    startX.current = t.clientX
    startY.current = t.clientY
    lock.current = null
    isDragging.current = true
    currentDragX.current = 0
  }, [])

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging.current) return
    const t = e.touches[0]
    const dx = t.clientX - startX.current
    const dy = t.clientY - startY.current

    if (lock.current === null) {
      if (Math.abs(dx) < LOCK_DISTANCE && Math.abs(dy) < LOCK_DISTANCE) return
      lock.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
    }

    if (lock.current === 'h') {
      e.preventDefault()
      currentDragX.current = dx
      setDragX(dx)
    }
  }, [])

  const onTouchEnd = useCallback(() => {
    if (!isDragging.current) return
    isDragging.current = false

    if (lock.current !== 'h') {
      setDragX(0)
      return
    }

    const dx = currentDragX.current
    // Wrap navigation in a View Transition so the snapshot of the dragged
    // content slides off-screen while the new day slides in from the
    // opposite side — feels like a continuation of the swipe instead of a
    // cut. Drag offset reset is folded into the transition callback so the
    // OLD snapshot keeps the dragged frame and only the NEW snapshot is at 0.
    if (dx > THRESHOLD_PX) {
      withViewTransition(() => {
        setDragX(0)
        onPrev()
      }, 'back')
    } else if (dx < -THRESHOLD_PX) {
      withViewTransition(() => {
        setDragX(0)
        onNext()
      }, 'forward')
    } else {
      setDragX(0)
    }
  }, [onPrev, onNext])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [onTouchStart, onTouchMove, onTouchEnd])

  return (
    <div
      ref={containerRef}
      style={{
        transform: dragX === 0 ? undefined : `translate3d(${dragX}px, 0, 0)`,
        transition: isDragging.current ? 'none' : 'transform var(--duration-base) var(--ease-out-soft)',
        willChange: isDragging.current ? 'transform' : undefined,
      }}
    >
      {children}
    </div>
  )
}
